import { useCallback, useEffect, useMemo, useState } from 'react';
import useUser from '@alltrails/context/hooks/useUser';
import useFeaturePreview from '@alltrails/core/hooks/useFeaturePreview';
import { updateDynamicStyleIds } from '@alltrails/maps/redux/reducer';
import { useAppDispatch, useAppSelector } from '../redux/helpers';

const useHeatmapsPeakValidation = () => {
  const dispatch = useAppDispatch();
  const dynamicStyleIds = useAppSelector(state => state.map.dynamicStyleIds);
  const user = useUser();

  const onPreviewFinished = useCallback(() => {
    setShowHeatmapsUpsellModal(true);
    setHasShownHeatmapsUpsellModal(true);
  }, []);

  const { isPreviewActive, requestPreview, cancelPreview } = useFeaturePreview({
    featureEntitlement: 'community_heat_maps',
    onPreviewFinished
  });

  const [hasShownHeatmapsUpsellModal, setHasShownHeatmapsUpsellModal] = useState(false);
  const [showHeatmapsUpsellModal, setShowHeatmapsUpsellModal] = useState(false);
  const onHeatmapsUpsellModalCloseRequest = useCallback(() => setShowHeatmapsUpsellModal(false), []);
  const [heatmapsUpsellModalApplyStylesCallback, setHeatmapsUpsellModalApplyStylesCallback] = useState<() => void>();

  useEffect(() => {
    if (!dynamicStyleIds.includes('newHeatmap') && isPreviewActive) {
      // cancel the preview if the heatmap overlay is no longer applied
      cancelPreview();
    }
  }, [isPreviewActive, dynamicStyleIds, cancelPreview]);

  const removeAppliedHeatmaps = useCallback(() => {
    dispatch(updateDynamicStyleIds(dynamicStyleIds.filter(id => id !== 'newHeatmap')));
  }, [dispatch, dynamicStyleIds]);

  const handleHeatmapFailedEntitlementCheck = useCallback(
    async (applyStyles: () => void) => {
      const { isEligible } = await requestPreview();

      if (isEligible) {
        applyStyles();
      } else {
        setShowHeatmapsUpsellModal(true);
        setHasShownHeatmapsUpsellModal(true);
        setHeatmapsUpsellModalApplyStylesCallback(() => applyStyles);
      }
    },
    [requestPreview]
  );

  useEffect(() => {
    if (hasShownHeatmapsUpsellModal && !showHeatmapsUpsellModal) {
      // After the upsell modal is closed, if the user signed up for peak, apply the heatmap, otherwise, remove them
      if (user?.featureEntitlements?.includes('community_heat_maps')) {
        heatmapsUpsellModalApplyStylesCallback?.();
      } else {
        removeAppliedHeatmaps();
      }
      setHeatmapsUpsellModalApplyStylesCallback(undefined);
    }
    // We only want to check this when the modal closes.
    // The upsell modals currently do not support an onFailure/onSuccess callback, which would be preferred,
    // and we don't want to make a risky change to those upsells right before peak launch.
  }, [showHeatmapsUpsellModal]);

  return useMemo(
    () => ({
      showHeatmapsUpsellModal,
      onHeatmapsUpsellModalCloseRequest,
      handleHeatmapFailedEntitlementCheck,
      removeAppliedHeatmaps
    }),
    [handleHeatmapFailedEntitlementCheck, onHeatmapsUpsellModalCloseRequest, showHeatmapsUpsellModal, removeAppliedHeatmaps]
  );
};

export default useHeatmapsPeakValidation;
